import memoize from "lodash/memoize";

import { RegionSetParseResults } from "@app/projects/create/interfaces";
import ServiceBase from "@app/shared/serviceBase";
import RegionSet from "@app/region-sets/regionSet";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { Geometry } from "geojson";
import { CountyContextModel, CountyModel, ZipCodeModel } from "@app/projects/network-assessment/maps/network-coverage-map/interfaces";
import County, { CountyLite, GetNearbyCounties, GetNearbyCountiesWithinRange, CountyWithinRanges } from "@app/geography/county";
import { CountyZipAggregate } from "@app/geography/countyZipAggregate";
import { State, BasicState } from "@app/geography/state";
import Zip, { ZipLite } from "@app/geography/zip";

/** Handles retrieval operations for geography information. */
export default class GeographyService extends ServiceBase {
  /**
   * Retrieves detailed information for all 50 US states, plus District of Columbia, Puerto Rico, and the Virgin Islands.
   */
  public readonly getStates = memoize(async () => {
    return await this.makeGet<State[]>("states");
  });

  /**
   * Gets state abbreviation and name for all 50 US states, plus District of Columbia, Puerto Rico, and the Virgin Islands.
   */
  public readonly getStatesForDroplist = memoize(async () => {
    return await this.makeGet<BasicState[]>("states-list");
  });

  public readonly getCounties = memoize(async () => {
    return await this.makeGet<County[]>(`counties`);
  });

  public readonly getCountiesLite = memoize(async (usesPlanningRegion: boolean) => {
    // returns a stripped down County model
    return await this.makeGet<CountyLite[]>("counties-lite", { usesPlanningRegion });
  });

  /**
   * Retrieves all the counties for a given state.
   * @param stateAbbreviation The state abbreviation.
   */
  public readonly getCountiesByState = memoize(async (stateAbbreviation: string) => {
    return await this.makeGet<County[]>(`states/${stateAbbreviation}/counties`);
  });

  /**
   * Retrieves all the counties in a given CBSA.
   * @param cbsaCode The CBSA's identifying code.
   */
  public readonly getCountiesByCbsa = memoize(async (cbsaCode: string) => {
    return await this.makeGet<County[]>(`cbsas/${cbsaCode}/counties`);
  });

  /**
   * Retrieves all the counties for a given zip code (which may include non-primary).
   * @param zip The zip code.
   * @param includeNonPrimary Include non-primary or not?
   */
  public readonly getCountyByZip = memoize(async (zipCode: string) => {
    return await this.makeGet<County[]>(`zipcodes/${zipCode}/counties`);
  });

  /**
   * Retrieves all the counties and zips aggregates for a given zip code (including non-primary).
   * @param zip The zip code.
   * @param includeNonPrimary Include non-primary or not?
   */
  public readonly getCountyZipAggregateByZip = memoize(async (zipCode: string) => {
    return await this.makeGet<CountyZipAggregate>(`zipcodes/${zipCode}/countieszips`);
  });

  /**
   * Retrieves all the zips in a given state.
   * @param stateAbbreviation The state abbreviation.
   */
  public readonly getZipsByState = memoize(async (stateAbbreviation: string) => {
    return await this.makeGet<Zip[]>(`states/${stateAbbreviation}/zips`);
  });

  public readonly getZipsLite = memoize(async () => {
    return await this.makeGet<ZipLite[]>("zipcodes-lite");
  });

  /**
   * Retrieves all the extending zips for the given county.
   * @param countySSA The county's identifier.
   */
  public readonly getExtendingZipsByCounty = memoize(async (countySSA: string) => {
    return await this.makeGet<Zip[]>(`zipcodes/${countySSA}/extending`);
  });

  /**
   * Retrieves all the zips in a given county.
   * @param countySSA The county's identifier.
   */
  public readonly getZipsByCounty = memoize(async (countySSA: string) => {
    return await this.makeGet<Zip[]>(`counties/${countySSA}/zips`);
  });

  /**
   * Retrieves the region-set for the given region-set-id
   * @param regionSetId The region-set identifier.
   */
  public readonly getRegionSet = memoize(async (regionSetId: number) => {
    return await this.makeGet<RegionSet>(`regionSets/${regionSetId}`);
  });

  /**
   * Retrieves a data structure of selected regions that can be rendered as necessary for the UI
   * @param regionSetId The region-set identifier
   */
  public readonly getParsedRegionSet = memoize(async (regionSetId: number) => {
    return await this.makeGet<RegionSetParseResults>(`regionSets/${regionSetId}/Parse`);
  });

  /**
   * Retrieves the estimated driving distance from the source coordinates to the destination coordinates.
   * @param fromLongitude The source longitude.
   * @param fromLatitude The source latitude.
   * @param toLongitude The destination longitude.
   * @param toLatitude The destination latitude.
   */
  public readonly getDrivingDistance = memoize(
    async (fromLongitude: number, fromLatitude: number, toLongitude: number, toLatitude: number) => {
      return await this.makeGet<number>(`drivingdistance/${fromLongitude}/${fromLatitude}/${toLongitude}/${toLatitude}`);
    },
    (...args) => {
      return JSON.stringify(args);
    },
  );

  /**
   * Retrieves all the counties, within a given distance, from a source county.
   * @param countySSA The source county's identifier.
   * @param distance The distance to search.
   * @param includeSourceCounties If true, includes the source counties in the results.
   * @param limitToCurrentStates If true, only returns counties in the states of the region set.
   */
  public async getNearbyCounties(getNearbyCounties: GetNearbyCounties): Promise<County[]> {
    return await this.makePost<County[]>("counties/nearby", getNearbyCounties);
  }

  /**
   * Retrieves all the counties, within a given distance, from a source county within a given range (0, 50, 100, 150).
   * @param countySSA The source county's identifier.
   */
  public async getNearbyCountiesWithinRange(getNearbyCounties: GetNearbyCountiesWithinRange): Promise<CountyWithinRanges[]> {
    return await this.makePost<CountyWithinRanges[]>("counties/nearbyWithinRanges", getNearbyCounties);
  }

  /**
   * Retreives all children of the targetGranulity from the given region set.
   * @param regionSetId The ID of the region set to search.
   * @param targetGranularity The granularity to search for.
   */
  public async getGranularRegionSets(regionSetId: number, targetGranularity: string): Promise<RegionSet[]> {
    return await this.makeGet<RegionSet[]>(`RegionSets/${regionSetId}/ByGranularity/${targetGranularity}`);
  }

  /**
   * Retrieves an existing region set based on its model, or creates a new one.
   * @param regionSetModel The region set data you're trying to retrieve or store.
   */
  public async getOrCreateRegionSet(regionSetModel: RegionSet): Promise<RegionSet> {
    return await this.makePost<RegionSet>(`RegionSets/GetOrCreate`, regionSetModel);
  }

  /**
   * Retrieves existing region set based on the models, or creates a new one.
   * @param regionSetModel The region set data you're trying to retrieve or store.
   */
  public async getOrCreateRegionSets(regionSetModels: RegionSet[]): Promise<RegionSet[]> {
    return await this.makePost<RegionSet[]>(`RegionSets/GetOrCreateMultiple`, regionSetModels);
  }

  public async downloadServiceArea(projectName: string, regionSetId: number, countyStatus: { [countySSA: string]: RegionSetStatusType }): Promise<File> {
    const requestBody = {
      countyStatus,
      projectName,
      serviceAreaId: regionSetId,
    }

    return await this.makePost<File>("RegionSets/download-service-area", requestBody);
  }

  public async getStateBorders(): Promise<string> {
    return await this.makeOptionalGet<string>("states/borders") || null;
  }

  /**
   * Retrieves the borders for all counties in a given region set.
   * @param regionSetId The identifier of the region set.
   * @returns A GeoJSON FeatureCollection representing the borders of the counties in the region set. Will return null if none are found.
   */
  public async getCountyBordersByRegionSetId(regionSetId: string): Promise<GeoJSON.FeatureCollection<Geometry, CountyModel>> {
    return await this.makeOptionalGet<GeoJSON.FeatureCollection<Geometry, CountyModel>>(`RegionSets/${regionSetId}/Counties/borders`) || null;
  }

  public async getCountyBordersByRegionSetIds(regionSetIds: string[]): Promise<GeoJSON.FeatureCollection> {
    const requestBody = {
      regionSetIds
    }

    return await this.makeOptionalGet<GeoJSON.FeatureCollection>(`RegionSets/Counties/borders`, requestBody) || null;
  }

  /**
   * Retrieves the outlines of the other counties in the selected counties' states, of every county in
   * neighboring states and of those states, plus label points for the selected and same-state counties.
   */
  public async getCountyContextByRegionSetIds(regionSetIds: string[]): Promise<CountyContextModel> {
    const requestBody = {
      regionSetIds
    }

    return await this.makeOptionalGet<CountyContextModel>(`RegionSets/Counties/context`, requestBody) || null;
  }

  /**
   * Retrieves the zip code geo json for multiple counties.
   * @param countiesSSA The countiesSSA for the requested zip codes
   * @returns A GeoJson FeatureCollection representing the borders fo zipcodes within the counties requested. Will return null if none are found.
  */
  public async getZipCodeBordersByRegionSetId(regionSetId: string): Promise<GeoJSON.FeatureCollection<Geometry, ZipCodeModel>> {
    return await this.makeOptionalGet<GeoJSON.FeatureCollection<Geometry, ZipCodeModel>>(`RegionSets/${regionSetId}/ZipCodes/borders`) || null;
  }

  public async getZipCodeBordersByRegionSetIds(regionSetIds: string[]): Promise<GeoJSON.FeatureCollection<Geometry, ZipCodeModel>> {
    const requestBody = {
      regionSetIds
    }

    return await this.makeOptionalGet<GeoJSON.FeatureCollection<Geometry, ZipCodeModel>>(`RegionSets/ZipCodes/borders`, requestBody) || null;
  }

  /**
   * Retrieves the boundary GeoJSON for a single zip code.
   * @param zipCode The zip code to fetch the boundary for.
   * @returns A GeoJson FeatureCollection representing the boundary of the requested zip code. Will return null if none are found.
  */
  public async getZipCodeBorderByZipCode(zipCode: string): Promise<GeoJSON.FeatureCollection<Geometry, ZipCodeModel>> {
    return await this.makeOptionalGet<GeoJSON.FeatureCollection<Geometry, ZipCodeModel>>(`RegionSets/zipcodes/${zipCode}/Borders`) || null;
  }

  public async getZipCodeBordersByZipCodes(zipCodes: string[]): Promise<GeoJSON.FeatureCollection<Geometry, ZipCodeModel>> {
    return await this.makePost<GeoJSON.FeatureCollection<Geometry, ZipCodeModel>>(`RegionSets/zipcodes/borders`, zipCodes) || null;
  }

  public readonly getRegions = memoize(async (projectId: number, projectRunConfigurationId: number): Promise<RegionSet[]> => {
    return await this.makeGet<RegionSet[]>(`Projects/${projectId}/runconfigurations/${projectRunConfigurationId}/regions`) ?? [];
  }, (projectId, projectRunConfigurationId) => `${projectId}-${projectRunConfigurationId}`);

}